import { db, storage } from "../firebase-config";
import { collection, setDoc, query, addDoc, onSnapshot, doc, deleteDoc, getDoc, getDocs, where, serverTimestamp, updateDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject, getStorage } from 'firebase/storage';

// Snapshot listener for organization documents
export const organizationDocumentsSnapshotListener = (organization, setDocuments) => {
    const docsQuery = query(collection(db, "organizations", organization, "documents"));
    try {
        const unsubscribe = onSnapshot(docsQuery, (querySnapshot) => {
            const docs = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setDocuments(docs);
        });
        return () => unsubscribe();
    } catch (error) {
        setDocuments({ success: false, data: null, error: error.message || 'Failed to fetch documents' });
    }
};

// Snapshot listener for employee documents
export const employeeDocumentsSnapshotListener = (organization, employeeId, setDocuments) => {
    const docsQuery = query(collection(db, "organizations", organization, "employees", employeeId, "documents"));
    try {
        const unsubscribe = onSnapshot(docsQuery, (querySnapshot) => {
            const docs = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setDocuments(docs);
        });
        return () => unsubscribe();
    } catch (error) {
        setDocuments({ success: false, data: null, error: error.message || 'Failed to fetch employee documents' });
    }
};

// Helper function to get next available copy number
const getNextCopyNumber = async (documentsRef, baseTitle, baseName) => {
  const docs = await getDocs(query(documentsRef, 
    where('title', '>=', baseTitle),
    where('title', '<=', baseTitle + '\uf8ff')
  ));
  
  const copyNumbers = docs.docs
    .map(doc => {
      const match = doc.data().title.match(/\((\d+)\)$/);
      return match ? parseInt(match[1]) : 0;
    })
    .filter(num => !isNaN(num));

  return copyNumbers.length ? Math.max(...copyNumbers) + 1 : 1;
};

// Generic document upload function that handles both employee and organization documents
export const uploadDocumentAndReferenceInFirestore = async (
  organization,
  file,
  title,
  options = {
    type: 'organization', // or 'employee'
    employeeId: null,
    handleDuplicate: 'ask' // 'ask', 'overwrite', or 'copy'
  }
) => {
  try {
    // Determine the collection reference based on type
    const documentsRef = options.type === 'employee' 
      ? collection(db, "organizations", organization, "employees", options.employeeId, "documents")
      : collection(db, "organizations", organization, "documents");
    
    // Check for existing document
    const existingDocsSnapshot = await getDocs(query(documentsRef, 
      where('title', '==', title)
    ));

    if (!existingDocsSnapshot.empty && options.handleDuplicate === 'ask') {
      return {
        success: false,
        error: 'DUPLICATE_FILE',
        existingDoc: {
          id: existingDocsSnapshot.docs[0].id,
          ...existingDocsSnapshot.docs[0].data()
        }
      };
    }

    let finalTitle = title;
    let finalFileName = file.name;

    // Handle copy case
    if (options.handleDuplicate === 'copy') {
      const copyNum = await getNextCopyNumber(documentsRef, title);
      finalTitle = `${title} (${copyNum})`;
      finalFileName = `${file.name.replace(/\.[^/.]+$/, '')} (${copyNum})${file.name.match(/\.[^/.]+$/)[0]}`;
    }

    // Prepare storage path
    const filePath = options.type === 'employee'
      ? `/organizations/${organization}/employees/${options.employeeId}/documents/${finalFileName}`
      : `/organizations/${organization}/documents/${finalFileName}`;
    
    const storageRef = ref(storage, filePath);

    // Upload file
    await uploadBytes(storageRef, file);

    // Prepare document data
    const documentData = {
      name: finalFileName,
      title: finalTitle,
      file_path: filePath,
      uploaded_at: serverTimestamp(),
      uploaded_by: options.employeeId || 'system'
    };

    // Handle database update
    if (options.handleDuplicate === 'overwrite' && !existingDocsSnapshot.empty) {
      await updateDoc(existingDocsSnapshot.docs[0].ref, documentData);
      return { 
        success: true, 
        docId: existingDocsSnapshot.docs[0].id
      };
    } else {
      const docRef = await addDoc(documentsRef, documentData);
      return { 
        success: true, 
        docId: docRef.id
      };
    }

  } catch (error) {
    console.error("Error uploading document:", error);
    return { 
      success: false, 
      error: error.message || 'Failed to upload document' 
    };
  }
};

// Wrapper functions for backward compatibility
export const uploadEmployeeDocumentAndReferenceInFirestore = async (
  organization, 
  employeeId, 
  file, 
  title,
  handleDuplicate = 'ask'
) => {
  return uploadDocumentAndReferenceInFirestore(organization, file, title, {
    type: 'employee',
    employeeId,
    handleDuplicate
  });
};

export const uploadOrganizationDocumentAndReferenceInFirestore = async (
  organization, 
  employeeId,
  file, 
  title,
  handleDuplicate = 'ask'
) => {
  return uploadDocumentAndReferenceInFirestore(organization, file, title, {
    type: 'organization',
    employeeId,
    handleDuplicate
  });
};

// Set deleted field in Firestore document and delete file from storage
export const deleteOrganizationDocumentAndReferenceFromFirestore = async (organization, docId, fileName) => {
    try {
        const fileRef = ref(storage, `/organizations/${organization}/documents/${fileName}`);
        await deleteObject(fileRef);
        const docRef = doc(db, "organizations", organization, "documents", docId);
        await deleteDoc(docRef); 
        return { success: true, error: null };

    } catch (error) {
        return { success: false, error: error.message || 'Failed to delete document and file' };
    }
};

// Generic function to get document download URL
export const getDocumentDownloadURL = async (organization, docId, options = { type: 'organization', employeeId: null }) => {
    try {
        // Determine the document reference based on type
        const docRef = options.type === 'employee'
            ? doc(db, "organizations", organization, "employees", options.employeeId, "documents", docId)
            : doc(db, "organizations", organization, "documents", docId);
        
        const docSnap = await getDoc(docRef);
        
        if (!docSnap.exists()) {
            throw new Error('Document does not exist');
        }

        const documentData = docSnap.data();
        const storage = getStorage();
        const fileRef = ref(storage, documentData.file_path);
        const downloadURL = await getDownloadURL(fileRef);
        
        return { 
            success: true, 
            data: downloadURL, 
            error: null 
        };
    } catch (error) {
        console.error("Error getting document download URL:", error);
        return { 
            success: false, 
            data: null, 
            error: error.message 
        };
    }
};

// Wrapper functions for backward compatibility
export const getEmployeeDocumentDownloadURL = async (organization, employeeId, docId) => {
    return getDocumentDownloadURL(organization, docId, {
        type: 'employee',
        employeeId
    });
};

export const getOrganizationDocumentDownloadURL = async (organization, docId) => {
    return getDocumentDownloadURL(organization, docId, {
        type: 'organization'
    });
};

export const deleteEmployeeDocumentAndReferenceFromFirestore = async (organization, userId, docId, fileName) => {
    try {
        const fileRef = ref(storage, `/organizations/${organization}/employees/${userId}/documents/${fileName}`);
        await deleteObject(fileRef);
        const docRef = doc(db, "organizations", organization, "employees", userId, "documents", docId);
        await deleteDoc(docRef);
        return { success: true, error: null };
    } catch (error) {
        return { success: false, error: error.message || 'Failed to delete employee document' };
    }
};

// Upload an inventory image and add reference in Firestore
export const uploadInventoryImageAndReferenceInFirestore = async (organization, itemId, file) => {
    try {
        const storageRef = ref(storage, `/organizations/${organization}/inventory/${itemId}/image`);
        const snapshot = await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(snapshot.ref);
        const inventoryItemDocRef = doc(db, "organizations", organization, "inventory", itemId);
        await setDoc(inventoryItemDocRef, { image_url: downloadURL }, { merge: true });
        return { success: true, error: null };
    } catch (error) {
        return { success: false, error: error.message || 'Failed to upload inventory image' };
    }
};

// Upload an asset image and add reference in Firestore
export const uploadAssetImageAndReferenceInFirestore = async (organization, assetId, file) => {
    try {
        const storageRef = ref(storage, `/organizations/${organization}/assets/${assetId}/image`);
        const snapshot = await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(snapshot.ref);

        const imageDocRef = doc(db, "organizations", organization, "assets", assetId);
        await setDoc(imageDocRef, { image_url: downloadURL }, { merge: true });
        return { success: true, error: null };
    } catch (error) {
        return { success: false, error: error.message || 'Failed to upload asset image' };
    }
}